import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TextareaField } from './TextareaField';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';

export interface CuratorshipItem {
  id?: string;
  title: string;
  url: string;
  description: string;
}

interface CuratorshipItemsListInputProps {
  value?: CuratorshipItem[];
  onChange: (_items: CuratorshipItem[]) => void;
}

const emptyItem = (): CuratorshipItem => ({
  id: crypto.randomUUID(),
  title: '',
  url: '',
  description: '',
});

export const CuratorshipItemsListInput: React.FC<CuratorshipItemsListInputProps> = ({
  value = [],
  onChange,
}) => {
  const [items, setItems] = useState<CuratorshipItem[]>(value);

  useEffect(() => {
    const list = value ?? [];
    if (list.length === 0) {
      setItems([]);
      return;
    }
    let next = list;
    if (list.some((it) => !it.id)) {
      next = list.map((it) => ({ ...it, id: it.id || crypto.randomUUID() }));
      onChange(next);
    }
    setItems(next);
  }, [value, onChange]);

  const addItem = () => {
    const next = [...items, emptyItem()];
    setItems(next);
    onChange(next);
  };

  const removeItem = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    setItems(next);
    onChange(next);
  };

  const updateItem = <K extends keyof CuratorshipItem>(index: number, field: K, fieldValue: CuratorshipItem[K]) => {
    const next = items.map((item, i) => (i === index ? { ...item, [field]: fieldValue } : item));
    setItems(next);
    onChange(next);
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const next = [...items];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    setItems(next);
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button type="button" variant="outline" size="sm" onClick={addItem} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Adicionar item
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Nenhum item adicionado
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={item.id ?? index} className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h4 className="text-sm font-semibold text-gray-700">Item {index + 1}</h4>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => moveItem(index, -1)}
                    disabled={index === 0}
                    className="shrink-0 px-2"
                    aria-label="Mover para cima"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => moveItem(index, 1)}
                    disabled={index === items.length - 1}
                    className="shrink-0 px-2"
                    aria-label="Mover para baixo"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Input
                placeholder="Título do link (texto exibido)"
                value={item.title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(index, 'title', e.target.value)}
              />
              <Input
                placeholder="URL do conteúdo"
                value={item.url}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(index, 'url', e.target.value)}
              />
              <TextareaField
                label="Descrição (opcional)"
                value={item.description}
                onChange={(v) => updateItem(index, 'description', v)}
                placeholder="Texto após o título"
                rows={2}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
